import React, { FunctionComponent } from 'react';

import { Text } from 'components/chroma';

import { ApiData } from 'scenes/common/types';
import { getApiData } from 'scenes/common/helpers/apiData';

import {
  MotorFullPageProps,
  MotorPolicySubSection,
  MotorQuoteSubSection,
  MotorFunnelState,
  ReferenceData,
  MotorSection,
} from 'scenes/motor/types';
import {
  getOvernightParkingAddress,
  isVehicleManualEntry,
  getVehicleManuallyEntered,
  getVehicleFromSearch,
  getMissingLicencePlate,
  isVehicleUsedForBusiness,
  getBusinessType,
  isSmallBusiness,
  getVehicleUnderFinance,
  getVehicleFinancier,
  hasPreExistingDamage,
  getVehicleModifications,
  getSelectedSpecialFeatures,
  isVehicleUnderFinance,
  hasInsuredSincePossess,
  isNswParkingAddress,
  isComprehensiveSelected,
  isInsuredSincePossessed,
  getLicencePlate,
  hasModifications,
  hasSpecialFeatures,
} from 'scenes/motor/selectors';
import SummaryCard from 'components/SummaryCard';

const getBusinessTypeDescription = (
  funnelState: MotorFunnelState,
  referenceData: ApiData<ReferenceData>
) => {
  const businessType = getBusinessType(funnelState);
  return (
    getApiData(referenceData).businessTypes.find(
      (type) => type.value === businessType
    )?.label ?? businessType
  );
};

export const CarSummary: FunctionComponent<Pick<
  MotorFullPageProps,
  'funnelState' | 'onEditQuoteDetails' | 'onEditPolicyDetails' | 'referenceData'
>> = ({
  funnelState,
  onEditQuoteDetails,
  onEditPolicyDetails,
  referenceData,
}) => {
  const vehicle = isVehicleManualEntry(funnelState)
    ? getVehicleManuallyEntered(funnelState)
    : getVehicleFromSearch(funnelState);

  return (
    <>
      <SummaryCard
        heading="Parked overnight"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.CarDetails,
            MotorQuoteSubSection.OvernightParkingAddress
          )
        }
      >
        <Text>{getOvernightParkingAddress(funnelState).address}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Car details"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.CarDetails,
            isVehicleManualEntry(funnelState)
              ? MotorQuoteSubSection.VehicleManualSelect
              : MotorQuoteSubSection.VehicleDetails
          )
        }
      >
        <Text>{`${vehicle.year} ${vehicle.make} ${vehicle.model}`}</Text>
        <Text>{`${vehicle.bodyType}`}</Text>
        <Text>{`Registration: ${
          getLicencePlate(funnelState) ||
          getMissingLicencePlate(funnelState) ||
          'Not provided'
        }`}</Text>
      </SummaryCard>
      <SummaryCard
        heading="Business use"
        onEditClick={() =>
          onEditQuoteDetails(
            MotorSection.CarDetails,
            MotorQuoteSubSection.IsUsedForBusiness
          )
        }
      >
        <Text>{isVehicleUsedForBusiness(funnelState) ? 'Yes' : 'No'}</Text>
      </SummaryCard>
      {isVehicleUsedForBusiness(funnelState) && (
        <SummaryCard
          heading="Type of business"
          onEditClick={() =>
            onEditQuoteDetails(
              MotorSection.CarDetails,
              MotorQuoteSubSection.BusinessType
            )
          }
        >
          <Text>{getBusinessTypeDescription(funnelState, referenceData)}</Text>
        </SummaryCard>
      )}
      {isVehicleUsedForBusiness(funnelState) &&
        isNswParkingAddress(funnelState) && (
          <SummaryCard
            heading="Are you small business?"
            onEditClick={() =>
              onEditQuoteDetails(
                MotorSection.CarDetails,
                MotorQuoteSubSection.BusinessType
              )
            }
          >
            <Text>{isSmallBusiness(funnelState) ? 'Yes' : 'No'}</Text>
          </SummaryCard>
        )}
      {isComprehensiveSelected(funnelState) && (
        <>
          <SummaryCard
            heading="Type of finance"
            onEditClick={() =>
              onEditQuoteDetails(
                MotorSection.CarDetails,
                MotorQuoteSubSection.FinanceType
              )
            }
          >
            <Text>{getVehicleUnderFinance(funnelState)}</Text>
          </SummaryCard>
          {isVehicleUnderFinance(funnelState) && (
            <SummaryCard
              heading="Financed with?"
              onEditClick={() =>
                onEditQuoteDetails(
                  MotorSection.CarDetails,
                  MotorQuoteSubSection.VehicleFinance
                )
              }
            >
              <Text>{getVehicleFinancier(funnelState)}</Text>
            </SummaryCard>
          )}
          {hasInsuredSincePossess(funnelState) && (
            <SummaryCard
              heading="Has the car been comprehensively
      insured for the whole time between when you took possession of it and now?"
              onEditClick={() =>
                onEditPolicyDetails(
                  MotorSection.CarDetails,
                  MotorPolicySubSection.InsuredSincePossessed
                )
              }
            >
              <Text>{isInsuredSincePossessed(funnelState) ? 'Yes' : 'No'}</Text>
            </SummaryCard>
          )}
          <SummaryCard
            heading="Any rust, hail, panel or accident damage?"
            onEditClick={() =>
              onEditPolicyDetails(
                MotorSection.CarDetails,
                MotorPolicySubSection.PreExistingDamage
              )
            }
          >
            <Text>{hasPreExistingDamage(funnelState) ? 'Yes' : 'No'}</Text>
          </SummaryCard>
          <SummaryCard
            heading="Standard modifications or accessories"
            onEditClick={() =>
              onEditPolicyDetails(
                MotorSection.CarDetails,
                MotorPolicySubSection.VehicleModifications
              )
            }
          >
            {hasModifications(funnelState) &&
            getVehicleModifications(funnelState).length > 0 ? (
              getVehicleModifications(funnelState).map((mod) => (
                <Text key={mod.name}>{mod.description}</Text>
              ))
            ) : (
              <Text>None</Text>
            )}
          </SummaryCard>
          <SummaryCard
            heading="Any other special features"
            onEditClick={() =>
              onEditPolicyDetails(
                MotorSection.CarDetails,
                MotorPolicySubSection.SpecialFeatures
              )
            }
          >
            <Text>
              {hasSpecialFeatures(funnelState)
                ? getSelectedSpecialFeatures(funnelState)
                : 'None'}
            </Text>
          </SummaryCard>
        </>
      )}
    </>
  );
};

export default CarSummary;
